import { useEffect, useState } from "react";
import { useParams } from "wouter";
import { useGetProject, useListProjectFiles } from "@vorloop/api-client-react";
import { ChatDock } from "../components/chat/ChatDock";
import { TopBar, type AgentMode, type CenterTab, type DeviceMode } from "../components/editor/TopBar";
import { CanvasPane } from "../components/editor/CanvasPane";
import { RightPanel } from "../components/editor/RightPanel";
import { TerminalDock } from "../components/editor/TerminalDock";
import { TaskRail } from "../components/editor/TaskRail";
import { TaskBoard } from "../components/editor/TaskBoard";
import { MultiTabFileEditor } from "../components/files/MultiTabFileEditor";
import { SplitPane } from "../components/layout/SplitPane";
import { useProjectEvents } from "../hooks/useProjectEvents";

const TASKRAIL_COLLAPSED_KEY = "vp.tasks.rail.collapsed";

/** Candidate entry files our Expo bundler probes for. Keep in sync with
 * CANDIDATE_ENTRIES in apps/api-server/src/lib/expo-bundler.ts. */
const MOBILE_ENTRIES = new Set([
  "app/(tabs)/index.tsx",
  "app/tabs/index.tsx",
  "app/index.tsx",
  "App.tsx",
  "index.tsx",
]);

/**
 * IDE workspace — Replit-style 3-pane with drag-resizable splits:
 *   ┌──── chat ────┬──── canvas / editor ────┬──── right panel ────┐
 *   │  resizable   │  flex                    │  resizable          │
 *   └──────────────┴──────────────────────────┴─────────────────────┘
 */
export function Editor() {
  const params = useParams<{ projectId: string }>();
  const projectId = params.projectId;
  const { data: project, isLoading, error } = useGetProject(projectId);

  const [agentMode, setAgentMode] = useState<AgentMode>("agent-2");
  const [taskBoardOpen, setTaskBoardOpen] = useState(false);
  const [taskRailCollapsed, setTaskRailCollapsed] = useState(() => {
    try {
      return localStorage.getItem(TASKRAIL_COLLAPSED_KEY) === "1";
    } catch {
      return false;
    }
  });
  function toggleTaskRail() {
    setTaskRailCollapsed((v) => {
      const next = !v;
      try {
        localStorage.setItem(TASKRAIL_COLLAPSED_KEY, next ? "1" : "0");
      } catch {
        // ignore
      }
      return next;
    });
  }
  // Replit-style: keep a strip of open tabs in the TopBar; "preview"
  // is always first and not closable.
  const [openCenterTabs, setOpenCenterTabs] = useState<CenterTab[]>(["preview"]);
  const [centerTab, setCenterTab] = useState<CenterTab>("preview");
  const [deviceMode, setDeviceMode] = useState<DeviceMode>("ios");
  const [openTabs, setOpenTabs] = useState<string[]>([]);
  const [activePath, setActivePath] = useState<string | null>(null);
  const [previewSeq, setPreviewSeq] = useState(0);

  function openCenterTab(t: CenterTab) {
    setOpenCenterTabs((arr) => (arr.includes(t) ? arr : [...arr, t]));
    setCenterTab(t);
  }
  function closeCenterTab(t: CenterTab) {
    if (t === "preview") return;
    setOpenCenterTabs((arr) => {
      const next = arr.filter((x) => x !== t);
      if (t === centerTab) setCenterTab(next[next.length - 1] ?? "preview");
      return next;
    });
  }

  function openFile(path: string) {
    setOpenTabs((arr) => (arr.includes(path) ? arr : [...arr, path]));
    setActivePath(path);
  }
  function closeFile(path: string) {
    setOpenTabs((arr) => {
      const next = arr.filter((p) => p !== path);
      if (path === activePath) {
        const fallback = next[next.length - 1] ?? null;
        setActivePath(fallback);
      }
      return next;
    });
  }
  function closeAllFiles() {
    setOpenTabs([]);
    setActivePath(null);
  }

  // Subscribe to the project's SSE event channel so file changes from
  // OTHER sessions (or the agent loop in this one) invalidate caches.
  // run_preview events bump the iframe explicitly.
  useProjectEvents(projectId, {
    onPreviewRefresh: () => setPreviewSeq((n) => n + 1),
  });

  // File-tree query is invalidated by ChatDock whenever the agent
  // writes/edits/deletes; piggy-back on dataUpdatedAt to bump the
  // iframe so the preview reloads.
  const files = useListProjectFiles(projectId);
  useEffect(() => {
    if (!files.dataUpdatedAt) return;
    setPreviewSeq((n) => n + 1);
  }, [files.dataUpdatedAt]);

  const hasMobileEntry =
    files.data?.items.some((f) =>
      MOBILE_ENTRIES.has(f.path),
    ) ?? false;

  if (isLoading) return <FullPageMessage>Loading project…</FullPageMessage>;
  if (error) return <FullPageMessage error>{error.message}</FullPageMessage>;
  if (!project) return <FullPageMessage error>Project not found.</FullPageMessage>;

  const center = openTabs.length > 0 ? (
    <MultiTabFileEditor
      projectId={project.id}
      openTabs={openTabs}
      activePath={activePath}
      onActivate={setActivePath}
      onClose={(p) => {
        closeFile(p);
        if (openTabs.length === 1) setCenterTab("preview");
      }}
      onCloseAll={() => {
        closeAllFiles();
        setCenterTab("preview");
      }}
    />
  ) : (
    <CanvasPane
      projectId={project.id}
      projectName={project.name}
      projectType={project.type}
      hasMobileEntry={hasMobileEntry}
      tab={centerTab}
      device={deviceMode}
      previewSeq={previewSeq}
      onChangeTab={openCenterTab}
    />
  );

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "var(--bg-canvas)",
        color: "var(--text-primary)",
        overflow: "hidden",
      }}
    >
      <TopBar
        projectId={project.id}
        projectName={project.name}
        designName={project.type === "mobile" ? "Mobile" : "Web"}
        agentMode={agentMode}
        onAgentModeChange={setAgentMode}
        openCenterTabs={openCenterTabs}
        centerTab={centerTab}
        onCenterTabChange={(t) => {
          openCenterTab(t);
          if (t !== "tools") closeAllFiles();
        }}
        onCloseCenterTab={closeCenterTab}
        onAddCenterTab={() => openCenterTab("tools")}
        deviceMode={deviceMode}
        onDeviceModeChange={setDeviceMode}
      />

      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
        {/* LEFTMOST — task rail (its own column, Replit-style) */}
        <div
          style={{
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            borderRight: "1px solid var(--border-default)",
            background: "var(--bg-panel)",
          }}
        >
          <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <TaskRail
              projectId={project.id}
              projectName={project.name}
              onOpenBoard={() => setTaskBoardOpen((v) => !v)}
              boardOpen={taskBoardOpen}
              collapsed={taskRailCollapsed}
              onToggleCollapsed={toggleTaskRail}
            />
          </div>
        </div>
        <SplitPane side="left" defaultSize={380} min={280} max={640} storageKey="vp.editor.chat">
          <div
            style={{
              flex: 1,
              minHeight: 0,
              borderRight: "1px solid var(--border-default)",
              background: "var(--bg-panel)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <ChatDock projectId={project.id} />
          </div>

          <SplitPane side="right" defaultSize={300} min={240} max={520} storageKey="vp.editor.right">
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div style={{ flex: 1, minHeight: 0, display: "flex" }}>{center}</div>
              <TerminalDock projectId={project.id} />
            </div>
            <RightPanel
              projectId={project.id}
              selectedPath={activePath}
              onSelectPath={(p) => {
                openFile(p);
                setCenterTab("tools");
              }}
            />
          </SplitPane>
        </SplitPane>
      </div>
      {taskBoardOpen && (
        <TaskBoard
          projectId={project.id}
          projectName={project.name}
          onClose={() => setTaskBoardOpen(false)}
        />
      )}
    </div>
  );
}

function FullPageMessage({
  children,
  error,
}: {
  children: React.ReactNode;
  error?: boolean;
}) {
  return (
    <div
      style={{
        height: "100vh",
        display: "grid",
        placeItems: "center",
        background: "var(--bg-canvas)",
        color: error ? "var(--accent-red)" : "var(--text-tertiary)",
        fontSize: 13,
      }}
    >
      {children}
    </div>
  );
}
